/* @flow */
import * as React from 'react';
import Emoji from 'components/Emoji.jsx';
import ShortcodeEmoji from 'components/ShortcodeEmoji.jsx';
import Link from 'components/Link.jsx';
import emojiAnnotationMap from 'emoji-annotation-to-unicode';
import matchStrings from 'utils/matchStrings';
import { replace as emojiReplace } from 'lib/emoji';
import Linkify from 'linkify-it';

const linkify = Linkify();

export type EntityType = {
  offset: number,
  length: number,
  renderer: Function,
  data?: Object,
};

export type ParserType = (text: string, bodyAttributes?: BodyAttribute[]) => EntityType[];

function renderEmoji(char: string) {
  return <Emoji character={char} />;
}

function renderShortcodeEmoji(code: string) {
  return <ShortcodeEmoji text={code} />;
}

function renderLink(text, rendered, data) {
  return (
    <Link href={data.url} target="_blank">
      {rendered}
    </Link>
  );
}

function renderNewline() {
  return <br />;
}

function renderStrong(text, rendered) {
  return <strong>{rendered}</strong>;
}

function renderWhitespace(text) {
  return text.replace(/\t/g, '\u00a0\u00a0').replace(/ /g, '\u00a0');
}

/**
 * @param  {RegExp} regExp   without any capture groups (2nd arg of replace fn must be index)
 * @param  {function} filterFn whether the match qualifies for an entity replacement
 * @param  {function} renderer to render in place of the matching text
 * @return {Entities[]}
 */
function createParser(regExp, filterFn, renderer): ParserType {
  return (text: string) => {
    const entities = [];
    if (text) {
      // $FlowFixMe
      text.replace(regExp, (match, index) => {
        if (!filterFn || filterFn(match)) {
          entities.push({
            offset: index,
            length: match.length,
            renderer,
          });
        }
      });
    }
    return entities;
  };
}

function createUnicodeEmojiParser(): ParserType {
  // like createParser but use the standard emoji replacer,
  // since we don't have access to the regexp it uses.
  return (text: string) => {
    const entities = [];
    emojiReplace(text, (match, index) => {
      entities.push({
        offset: index,
        length: match.length,
        renderer: renderEmoji,
      });
    });
    return entities;
  };
}

function createShortcodeEmojiParser(): ParserType {
  const keys = matchStrings(Object.keys(emojiAnnotationMap));
  const regExp = new RegExp(`:(?:${keys}):`, 'g');
  return createParser(regExp, null, renderShortcodeEmoji);
}

function getTrackedLinkAttributes(attributes: BodyAttribute[]): TrackedLinkAttribute[] {
  // $FlowFixMe override the return type since we know they're tracking links
  return (attributes.filter(attrib => attrib.type === 'tracking_link'): TrackedLinkAttribute[]);
}

const parseUrls: ParserType = (text: string, attributes?: BodyAttribute[]): EntityType[] => {
  // Nothing to parse if there's no text
  if (!text) {
    return [];
  }

  const trackedEntities = getTrackedLinkAttributes(attributes || []).reduce((acc, link) => {
    const lastOffset = acc.length > 0 ? acc[acc.length - 1].offset : -1;
    const index = text.indexOf(link.original_url, lastOffset);
    if (index > lastOffset) {
      return acc.concat({
        offset: index,
        length: link.original_url.length,
        data: { url: link.url },
        renderer: renderLink,
      });
    }
    return acc;
  }, []);

  const urlEntities = (linkify.match(text) || []).map(match => ({
    offset: match.index,
    length: match.text.length,
    data: { url: match.url },
    renderer: renderLink,
  }));

  // Merge arrays and filter out overlapping entities
  return [].concat(trackedEntities, urlEntities).reduce((acc, entity) => {
    const overlapsExisting = acc.some(existingEntity => {
      const newEntityOffsetWithinExistingEntity =
        entity.offset >= existingEntity.offset &&
        entity.offset <= existingEntity.offset + existingEntity.length;
      const existingEntityOffsetWithinNewEntity =
        existingEntity.offset >= entity.offset &&
        existingEntity.offset <= entity.offset + entity.length;
      return newEntityOffsetWithinExistingEntity || existingEntityOffsetWithinNewEntity;
    });
    if (!overlapsExisting) {
      return acc.concat(entity);
    } else {
      return acc;
    }
  }, []);
};

export default {
  parseShortcodeEmoji: createShortcodeEmojiParser(),
  parseNewlines: createParser(/\n/g, null, renderNewline),
  parseLeadingWhitespace: createParser(/^[\t ]+/gm, null, renderWhitespace),
  parseUrls,
  parseAttributes(attributes: BodyAttribute[], renderLinkTag: Function): EntityType[] {
    return attributes.reduce((result, item) => {
      switch (item.type) {
        case 'link':
          // $FlowFixMe cast
          const linkAttrib = (item: LinkAttribute);
          result.push({
            offset: linkAttrib.offset,
            length: linkAttrib.length,
            renderer: renderLinkTag,
            data: { ...linkAttrib, ...linkAttrib.data },
          });
          break;
        default:
          break;
      }
      return result;
    }, []);
  },
  createTextMatchingParser(text: string): ParserType {
    const replacedText = text.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&');
    return createParser(new RegExp(replacedText, 'ig'), null, renderStrong);
  },
  parseEmoji: createUnicodeEmojiParser(),
};
